import React, { useCallback, useEffect } from 'react';
import { useReplyToActions } from '../../contexts/ReplyToContext';
import { usePusher } from '../../contexts/PusherContext';
import { AgentEvents } from '@dreamai/types';
import { ReactComponent as ArrowLeftIcon } from '../../icons/arrow-left.svg';
import './routines-section.scss';

const ROUTINE_SUGGESTIONS = [
  'Every Monday at 8am, send me a summary of my week ahead via WhatsApp...',
  'When I get an email from my accountant, flag it and message me on WhatsApp...',
  'Every Friday at 5pm, email me a P&L snapshot for my business...',
  "At 9pm every Sunday, send me a WhatsApp with the kids' school week schedule...",
];

interface RoutineCreateViewProps {
  onBack: () => void;
  onRoutineCreated?: (autopilotId: string, routineName: string) => void;
}

const ROUTINE_REPLY_TO_ID = 'routine-creation';

const RoutineCreateView: React.FC<RoutineCreateViewProps> = ({
  onBack,
  onRoutineCreated,
}) => {
  const { addReplyTo, removeReplyTo } = useReplyToActions();
  const pusherContext = usePusher(true);
  const userChannel = pusherContext?.userChannel;

  useEffect(() => {
    addReplyTo({
      id: ROUTINE_REPLY_TO_ID,
      actionLine: 'Let me know what you want your routine to do',
      descriptionLine: '',
      extraContext:
        'The user is on the "Create routine" page and is discussing with you creating routines',
      persistent: true,
      glow: true,
    });

    return () => {
      removeReplyTo(ROUTINE_REPLY_TO_ID);
    };
  }, [addReplyTo, removeReplyTo]);

  useEffect(() => {
    if (!userChannel) return;

    const handleThreadCreated = (data: any) => {
      if (!data?.thread) return;
      const thread = data.thread;
      const discussingObjects =
        thread.discussing_objects || thread.discussingObjects;
      if (!Array.isArray(discussingObjects)) return;

      const hasAutopilot = discussingObjects.some(
        (obj: any) =>
          obj.discussing_object === 'Autopilot' ||
          obj.discussingObject === 'Autopilot',
      );

      if (hasAutopilot && onRoutineCreated) {
        const autopilotObj = discussingObjects.find(
          (obj: any) =>
            obj.discussing_object === 'Autopilot' ||
            obj.discussingObject === 'Autopilot',
        );
        const autopilotId =
          autopilotObj?.discussing_object_match?.autopilotId ||
          autopilotObj?.discussingObjectMatch?.autopilotId;
        if (autopilotId) {
          removeReplyTo(ROUTINE_REPLY_TO_ID);
          onRoutineCreated(autopilotId, thread.description || '');
        }
      }
    };

    userChannel.bind(AgentEvents.THREAD_CREATED, handleThreadCreated);
    return () => {
      userChannel.unbind(AgentEvents.THREAD_CREATED, handleThreadCreated);
    };
  }, [userChannel]);

  const handleBack = () => {
    removeReplyTo(ROUTINE_REPLY_TO_ID);
    onBack();
  };

  const handleSuggestionClick = useCallback((text: string) => {
    window.dispatchEvent(new CustomEvent('fill-chat-input', { detail: text }));
  }, []);

  return (
    <div className='routine-create-view'>
      <button
        type='button'
        className='routine-create-view-back'
        onClick={handleBack}
      >
        <ArrowLeftIcon />
        Back
      </button>

      <div className='routine-create-view-content'>
        <span className='routine-create-view-subtitle'>CUSTOM ROUTINE</span>
        <h2 className='routine-create-view-title'>Create routine</h2>
        <p className='routine-create-view-body'>
          You can setup a routine by explaining to your secretary what you'll
          need, they'll know what to do.
        </p>
      </div>

      <div className='routine-create-view-suggestions'>
        <span className='routine-create-view-suggestions-title'>
          Why not try...
        </span>
        {ROUTINE_SUGGESTIONS.map((text) => (
          <button
            key={text}
            type='button'
            className='routine-create-view-suggestion-bubble'
            onClick={() => handleSuggestionClick(text)}
          >
            {text}
          </button>
        ))}
      </div>
    </div>
  );
};

export default RoutineCreateView;
